import { useEffect, useMemo, useRef, useState, type MouseEvent, type ReactNode } from 'react'
import { ApiError } from '../lib/api'
import '../styles/lists.css'
import { errorInfoOf, markErrorShown } from '../lib/feedback'
import { ErrorReference } from './ui'
import { Pager } from './kit/Pager'
import { ScrollPanel } from './kit/ScrollPanel'
import { keepPanelY, pageRows, panelYOf, rowPressOpens } from '../lib/tablePlace'

export type Column<T> = {
  key: string
  header: string
  /** Sortable columns need a comparable value; the rendered cell is often markup. */
  sortValue?: (row: T) => string | number | null | undefined
  numeric?: boolean
  render: (row: T) => ReactNode
}

export type Sort = { key: string; dir: 'asc' | 'desc' }

// The way out of an empty table: a link, or a button where the way out
// rewrites the address in place (clearing a list's filters is not a history
// step).
export type EmptyAction = { label: string; href: string } | { label: string; onClick: () => void }
export type Empty = { title: string; detail: ReactNode; action?: EmptyAction; also?: EmptyAction }

type Props<T> = {
  caption: string
  columns: Column<T>[]
  rows: T[]
  rowKey: (row: T) => string
  // An empty table is a place somebody has arrived at and cannot leave.
  //
  // Title and detail describe the hole. `action` is the way out of it, and it
  // is the difference between a screen that reports a state and a screen that
  // does something about it. It is optional because some emptiness has no next
  // step worth naming: nothing has been closed yet is not a call to close
  // something.
  empty: Empty
  defaultSort?: Sort
  // The sort held by the caller (the register keeps it in its address); the
  // table reports a header press through onSort instead of keeping it.
  sort?: Sort
  onSort?: (sort: Sort) => void
  // Pages (release 3.3, V3): the rows of one page in a scroll panel whose
  // header row stays in view, the kit Pager under it, and the panel's place
  // kept in the history entry so Back lands on the same row. The caller keeps
  // the page and size (in its address) and is told of a change. `scope`
  // names the list (its filters and search): a new list, like a new page, a
  // new size or a new sort, opens the panel at its first row.
  paging?: { label: string; page: number; size: number; scope?: string; onPage: (page: number) => void; onSize: (size: number) => void }
  // A row opens this address on a plain press anywhere in it that is not a
  // control. Mouse only: the record's link in the row is the keyboard's way
  // in, so a row is never a second tab stop (lib/tablePlace.ts, rowPressOpens).
  rowHref?: (row: T) => string
  // What sits under the rows: the count of what is listed, and anything that
  // qualifies it. At the foot and not over the table (observation 3.13 of 17
  // September 2026): the rows are what a reader came for, and "12 of 40" is
  // read after them, the way a printed register reads.
  footer?: ReactNode
  // Open on this many rows, in the order the table is sorted, with the rest
  // one press away (10 Oct 2026). A register of fifty rows was four screens
  // before its own count could be read. The line that offers the rest says how
  // many there are, so a short list is never mistaken for the whole of it, and
  // sorting still sorts all of them: the first rows are the first of every row.
  firstRows?: number
  // Rows held at the top whatever the sort, tinted, under a one-line group
  // heading (release 3.3: a vendor just added lands at the top of the
  // register, not at the bottom where its empty risk band would sort it). The
  // caller marks each pinned row in words as well, never by colour alone.
  pinned?: (row: T) => boolean
  pinnedLabel?: string
}

export function DataTable<T>({
  caption,
  columns,
  rows,
  rowKey,
  empty,
  defaultSort,
  footer,
  firstRows,
  pinned,
  pinnedLabel,
  sort: heldSort,
  onSort,
  paging,
  rowHref,
}: Props<T>) {
  const [ownSort, setSort] = useState<Sort | null>(defaultSort ?? null)
  const sort = heldSort ?? ownSort
  const [all, setAll] = useState(false)
  const box = useRef<HTMLDivElement>(null)
  const paged = paging != null
  const hasRows = rows.length > 0

  const ordered = useMemo(() => {
    if (!sort) return rows
    const col = columns.find((c) => c.key === sort.key)
    if (!col?.sortValue) return rows
    const factor = sort.dir === 'asc' ? 1 : -1
    return [...rows].sort((a, b) => {
      const av = col.sortValue!(a)
      const bv = col.sortValue!(b)
      // Missing values sort last in both directions. A vendor with no due date
      // is not the most urgent one, and flipping the sort must not make it so.
      const aEmpty = av === null || av === undefined || av === ''
      const bEmpty = bv === null || bv === undefined || bv === ''
      if (aEmpty && bEmpty) return 0
      if (aEmpty) return 1
      if (bEmpty) return -1
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * factor
      return String(av).localeCompare(String(bv), 'en', { numeric: true }) * factor
    })
  }, [rows, sort, columns])
  // The pinned rows first, each part in the order the table is sorted.
  const sorted = useMemo(
    () => (pinned ? [...ordered.filter((r) => pinned(r)), ...ordered.filter((r) => !pinned(r))] : ordered),
    [ordered, pinned],
  )
  const pinnedCount = pinned ? sorted.filter((r) => pinned(r)).length : 0

  function toggle(key: string) {
    const next: Sort = sort?.key === key ? { key, dir: sort.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }
    if (onSort) onSort(next)
    else setSort(next)
  }

  // A paged table keeps its scroll panel's place in the history entry: put
  // back once its rows are drawn, written as the reader scrolls, and written
  // at once on any press inside it (the press may open a record, and after
  // that the entry on screen is no longer this one).
  useEffect(() => {
    if (!paged || !hasRows) return
    const el = box.current?.querySelector<HTMLElement>('.kit-scroll')
    if (!el) return
    const y = panelYOf(history.state)
    if (y > 0) el.scrollTop = y
    let t = 0
    const onScroll = () => {
      window.clearTimeout(t)
      t = window.setTimeout(() => keepPanelY(el.scrollTop), 150)
    }
    el.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      window.clearTimeout(t)
      el.removeEventListener('scroll', onScroll)
    }
  }, [paged, hasRows])
  const keepPlace = () => {
    const el = box.current?.querySelector<HTMLElement>('.kit-scroll')
    if (el) keepPanelY(el.scrollTop)
  }

  const capped = !paging && firstRows != null && firstRows > 0 && sorted.length > firstRows
  const page = paging ? pageRows(sorted, paging.page, paging.size) : null
  const listed = page ? page.rows : capped && !all ? sorted.slice(0, firstRows) : sorted
  // Where this page starts in the sorted rows, for the pinned rows' marks.
  const offset = page ? page.first : 0

  if (rows.length === 0) {
    return (
      <>
        <EmptyBlock empty={empty} />
        {footer}
      </>
    )
  }

  const open = (row: T) => {
    if (rowHref) window.location.hash = rowHref(row)
  }
  const onRowClick = (row: T) => (e: MouseEvent<HTMLTableRowElement>) => {
    const opens = rowPressOpens({
      onControl: (e.target as HTMLElement).closest('a, button, input, select, textarea, label, summary') != null,
      selecting: (window.getSelection()?.toString() ?? '') !== '',
      button: e.button,
      ctrl: e.ctrlKey,
      meta: e.metaKey,
      shift: e.shiftKey,
      alt: e.altKey,
    })
    if (opens) open(row)
  }

  const table = (
      <div className="table-wrap">
        <table className="grid">
          <caption className="vh">{caption}</caption>
          <thead>
            <tr>
              {columns.map((c) => {
                const active = sort?.key === c.key
                const ariaSort = active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'
                return (
                  <th
                    key={c.key}
                    scope="col"
                    className={c.numeric ? 'num' : undefined}
                    aria-sort={c.sortValue ? ariaSort : undefined}
                  >
                    {c.sortValue ? (
                      <button
                        type="button"
                        className="th-sort"
                        onClick={() => toggle(c.key)}
                        title={'Sort by ' + c.header}
                      >
                        {c.header}
                        <span className="caret" aria-hidden="true">
                          {active ? (sort.dir === 'asc' ? '▲' : '▼') : '◆'}
                        </span>
                      </button>
                    ) : (
                      <span className="th-static">{c.header}</span>
                    )}
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {pinnedCount > offset && pinnedLabel && (
              <tr className="row-group">
                <th scope="colgroup" colSpan={columns.length}>
                  {pinnedLabel}
                </th>
              </tr>
            )}
            {listed.map((row, n) => {
              const i = offset + n
              const mark = i < pinnedCount ? 'row-pinned' : i === pinnedCount && pinnedCount > 0 ? 'row-after-pinned' : ''
              return (
                <tr
                  key={rowKey(row)}
                  className={(mark + (rowHref ? ' row-open' : '')).trim() || undefined}
                  onClick={rowHref ? onRowClick(row) : undefined}
                >
                  {columns.map((c) => (
                    <td key={c.key} className={c.numeric ? 'num' : undefined}>
                      {c.render(row)}
                    </td>
                  ))}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
  )

  return (
    <>
      {paging && page ? (
        // A press inside is noted before the link or the row opens the record.
        <div ref={box} onClickCapture={keepPlace}>
          <ScrollPanel label={paging.label} resetKey={[paging.scope ?? '', sort?.key ?? '', sort?.dir ?? '', page.page, paging.size].join('|')}>
            {table}
          </ScrollPanel>
          <Pager
            label={'Pages of ' + caption.toLowerCase()}
            page={page.page}
            size={paging.size}
            total={sorted.length}
            onPage={paging.onPage}
            onSize={paging.onSize}
          />
        </div>
      ) : (
        table
      )}
      {capped && (
        <p className="list-more">
          <button type="button" data-testid="list-all-rows" aria-expanded={all} onClick={() => setAll((v) => !v)}>
            {all ? 'Show the first ' + firstRows : 'Show all ' + sorted.length}
          </button>
          {!all && (
            <span className="muted">
              The first {firstRows} of {sorted.length} are listed
            </span>
          )}
        </p>
      )}
      {footer}
    </>
  )
}

// What an empty table says, and its way out. Exported so a view that is not a
// table (the register's risk grid) says it the same way.
export function EmptyBlock({ empty }: { empty: Empty }) {
  const acts = [empty.action, empty.also].filter((a): a is EmptyAction => a != null)
  return (
    <div className="empty">
      <strong>{empty.title}</strong>
      {empty.detail}
      {acts.length > 0 && (
        <span className="empty-acts">
          {acts.map((a, i) =>
            'href' in a ? (
              <a key={a.label} className={'button empty-do' + (i === 0 ? ' primary' : '')} href={a.href}>
                {a.label}
              </a>
            ) : (
              <button key={a.label} type="button" className={'empty-do' + (i === 0 ? ' primary' : '')} onClick={a.onClick}>
                {a.label}
              </button>
            ),
          )}
        </span>
      )}
    </div>
  )
}

// How many rows a list is showing, said once and at its foot: "12 of 40" when
// a filter or a page holds some back, "40 vendors" when nothing does. One
// component so every list in the console says it the same way and in the same
// place. The rest of the sentence (awaiting approval, a capped read) is the
// caller's, passed as children after the count.
export function ListCount({
  shown,
  total,
  noun,
  one,
  children,
}: {
  shown: number
  // Absent where the list has no larger whole to be a part of.
  total?: number
  // Plural, and the singular where it is not the plural less an s.
  noun?: string
  one?: string
  children?: ReactNode
}) {
  const n = total ?? shown
  // Nothing at all to count: the empty state above has already said so, and
  // "0 open" under "No incident is open" says it twice. "0 of 40" still shows.
  if (n === 0) return null
  const word = noun ? ' ' + (n === 1 ? (one ?? noun.replace(/s$/, '')) : noun) : ''
  return (
    <p className="list-count" role="status">
      {total !== undefined && shown !== total ? (
        <>
          <strong>{shown}</strong> of <strong>{total}</strong>
          {word}
        </>
      ) : (
        <>
          <strong>{n}</strong>
          {word}
        </>
      )}
      {children}
    </p>
  )
}

// The three states a data view can be in, kept beside the table because a
// screen that only knows how to render rows is a screen that shows a spinner
// forever when the API is down.

export function Loading({ what }: { what: string }) {
  return (
    <p className="loading" role="status">
      Loading {what}.
    </p>
  )
}

// The heading names what failed. Most callers were loading something, so that
// stays the default; a caller that was creating or recording passes its own
// verb, because "could not be loaded" over a refused write sends the reader
// looking at the wrong end of the request.
//
// Since release 3.1 it also carries the error code and the reference support
// looks the request up by, and it tells the feedback net (lib/feedback.ts)
// that this failure is shown, so the net raises no second message for it.
export function ErrorBox({ error, what, verb = 'loaded' }: { error: unknown; what: string; verb?: string }) {
  const api = error instanceof ApiError ? error : null
  const message = api ? api.message : error instanceof Error ? error.message : 'An unexpected failure occurred.'
  markErrorShown(error)
  const info = errorInfoOf(error)
  return (
    <div className="error-box" role="alert" data-testid="error-box">
      <h3>
        {what} could not be {verb}
      </h3>
      <p>{message}</p>
      <p className="muted">{api ? api.remedy : 'Reload the page. If it repeats, capture the browser console output.'}</p>
      <ErrorReference code={info.code} reference={info.reference} />
      {api && api.issues.length > 0 && (
        <ul>
          {api.issues.map((i) => (
            <li key={i.field + i.code}>
              <strong>{i.field}</strong> {i.detail}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export function CoverageCell({ value, floor = 70 }: { value: number | null; floor?: number }) {
  if (value === null) return <span className="muted">not scored</span>
  const clamped = Math.max(0, Math.min(100, value))
  return (
    <span className="cov">
      <span className="cov-num">{Math.round(clamped)}%</span>
      <span className={clamped < floor ? 'cov-bar under' : 'cov-bar'} aria-hidden="true">
        <i style={{ width: clamped + '%' }} />
      </span>
    </span>
  )
}
